'use client'

import { useState } from 'react'

import { FeatureTooltipProvider, type TooltipData } from '@/components/client/product/feature-tooltip'
import { useTranslation } from '@/hooks/client/use-translation'

import { PriceUpgradeComparisonSelect } from './price-upgrade-comparison-select'
import type { UpgradeComparison } from './upgrade-comparison'

interface PriceUpgradeHighlightsProps {
  modelName?: string
  levelName: string
  comparisons: UpgradeComparison[]
  tooltips: Record<string, TooltipData>
}

const DEFAULT_VISIBLE_COUNT = 3

export const PriceUpgradeHighlights = ({
  modelName,
  levelName,
  comparisons,
  tooltips,
}: PriceUpgradeHighlightsProps) => {
  const { t } = useTranslation()
  const [comparisonId, setComparisonId] = useState(comparisons[0]?.levelId)
  const [expanded, setExpanded] = useState(false)
  const comparison = comparisons.find((entry) => entry.levelId === comparisonId) ?? comparisons[0]
  // The builder now orders value changes before additions for the ladder panel;
  // the chips keep the alphabetical order this arm has always shown.
  const highlights = [...(comparison?.highlights ?? [])].sort((a, b) => a.name.localeCompare(b.name))
  const hiddenCount = Math.max(highlights.length - DEFAULT_VISIBLE_COUNT, 0)
  const visibleHighlights = expanded ? highlights : highlights.slice(0, DEFAULT_VISIBLE_COUNT)
  const selectedName = modelName ? `${modelName} ${levelName}` : levelName

  if (!comparison || comparisons.every((entry) => entry.highlights.length === 0)) {
    return null
  }

  return (
    <FeatureTooltipProvider tooltips={tooltips}>
      <div className="mt-4 mb-8">
        <PriceUpgradeComparisonSelect
          selectedName={selectedName}
          modelName={modelName}
          comparisons={comparisons}
          selectedComparison={comparison}
          onComparisonChange={(levelId) => {
            setComparisonId(levelId)
            setExpanded(false)
          }}
        />
        <div className="flex flex-wrap gap-1.5" aria-live="polite">
          {highlights.length === 0 && (
            <p className="text-navy-60 text-sm">
              {t('product.upgradeNoAdditionalFeatures', 'No additional listed features over {{name}}.', {
                name: comparison.levelName,
              })}
            </p>
          )}
          {visibleHighlights.map((highlight) => (
            <button
              key={highlight.name}
              type="button"
              className="text-navy cursor-help rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold whitespace-nowrap transition-colors hover:border-slate-300 hover:bg-slate-50"
              data-spec-tooltip-id={highlight.tooltipId}
              data-tracking="ignore"
            >
              {highlight.name}
              {highlight.value !== '✓' ? `: ${highlight.value}` : ''}
            </button>
          ))}
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((current) => !current)}
              aria-expanded={expanded}
              data-ht-disclosure-trigger="read-more"
              data-tracking="ignore"
              className="text-navy cursor-pointer rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm whitespace-nowrap transition-colors hover:border-slate-300 hover:bg-slate-50"
            >
              {expanded
                ? t('product.showLess', 'Show Less')
                : t('product.showMoreHighlights', 'Show {{count}} more', { count: String(hiddenCount) })}
            </button>
          )}
        </div>
      </div>
    </FeatureTooltipProvider>
  )
}
